import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { BRAND_ORANGE } from '../../../styles/portalTheme';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../../styles/portalListTable';
import ImageUpload from '../ManageEvent/components/ImageUpload';
import { useAddEventAttendeesMutation } from '../../../api/use-add-event-attendees-mutation';
import { useExtractUserDetailsMutation } from '../../../api/use-extract-user-details-mutation';
import {
  EXTRACT_USER_DETAILS_FILE_ACCEPT,
  EXTRACT_USER_DETAILS_FORMAT_HINT,
  isExtractUserDetailsFileAllowed,
  normalizeExtractedUser,
  validateEventAttendeesList,
} from '../../../services/extractUserDetails';
import { sanitizePhoneInput } from '../../../utils/formatPhoneDisplay';

const formatExtractedPhone = (phone) => {
  if (phone == null || String(phone).trim() === '') return '—';
  return String(phone);
};
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];
const DEFAULT_ROWS_PER_PAGE = 10;

const EditAttendeeDialog = ({ open, mode = 'edit', attendee, onClose, onSave }) => {
  const isAddMode = mode === 'add';
  const [form, setForm] = useState({ name: '', email: '', phoneNumber: '' });

  useEffect(() => {
    if (!open) return;
    setForm({
      name: attendee?.name ?? '',
      email: attendee?.email ?? '',
      phoneNumber: attendee?.phoneNumber ?? '',
    });
  }, [attendee, open]);

  const handleSave = () => {
    const name = form.name.trim();
    const email = form.email.trim();

    if (!name || !email) {
      toast.error('Name and email are required.');
      return;
    }

    onSave({
      ...(attendee ?? {}),
      name,
      email,
      phoneNumber: form.phoneNumber.trim() || null,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{isAddMode ? 'Add user' : 'Edit attendee'}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Phone number"
            type="tel"
            value={form.phoneNumber}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, phoneNumber: sanitizePhoneInput(e.target.value) }))
            }
            inputProps={{ inputMode: 'tel', pattern: '[0-9+\\s().-]*' }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            textTransform: 'none',
            bgcolor: BRAND_ORANGE,
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          {isAddMode ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddAttendeeModal = ({ open, onClose, eventId, onAttendeesAdded }) => {
  const extractUserDetailsMutation = useExtractUserDetailsMutation();
  const addEventAttendeesMutation = useAddEventAttendeesMutation({
    onSuccess: (result) => {
      toast.success(result.message || 'Attendees added successfully');
      resetState();
      onClose?.();
      onAttendeesAdded?.(result);
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to add attendees');
    },
  });
  const isExtracting = extractUserDetailsMutation.isPending;
  const isSubmitting = addEventAttendeesMutation.isPending;
  const isBusy = isExtracting || isSubmitting;

  const [users, setUsers] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [sourceType, setSourceType] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [attendeeForm, setAttendeeForm] = useState(null);

  const isReviewStep = users.length > 0;

  const resetState = () => {
    setUsers([]);
    setWarnings([]);
    setSourceType(null);
    setUploadError('');
    setSelectedFile(null);
    setPage(0);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    setAttendeeForm(null);
    extractUserDetailsMutation.reset();
    addEventAttendeesMutation.reset();
  };

  const handleClose = () => {
    if (isBusy) return;
    resetState();
    onClose?.();
  };

  const handleSubmitAttendees = () => {
    if (!eventId) {
      toast.error('Event is required to add attendees.');
      return;
    }

    const validation = validateEventAttendeesList(users);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    addEventAttendeesMutation.mutate({ eventId, users });
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return users.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, users]);

  const handleFileChange = async (file) => {
    setSelectedFile(file);
    setUploadError('');

    if (!file) return;

    try {
      const result = await extractUserDetailsMutation.mutateAsync(file);
      setUsers(result.users);
      setWarnings(result.warnings ?? []);
      setSourceType(result.sourceType ?? null);
      setPage(0);
      toast.success(result.message || 'User details extracted successfully');
    } catch (error) {
      setUsers([]);
      setWarnings([]);
      setSourceType(null);
      setUploadError(error?.message || 'Failed to extract user details.');
    }
  };

  const handleDeleteUser = (userId) => {
    setUsers((prev) => {
      const next = prev.filter((user) => user.id !== userId);
      const maxPage = Math.max(0, Math.ceil(next.length / rowsPerPage) - 1);
      setPage((current) => Math.min(current, maxPage));
      return next;
    });
  };

  const handleSaveAttendee = (userData) => {
    if (attendeeForm?.mode === 'add') {
      const newUser = normalizeExtractedUser(userData, users.length);
      setUsers((prev) => {
        const next = [...prev, newUser];
        setPage(Math.max(0, Math.ceil(next.length / rowsPerPage) - 1));
        return next;
      });
      toast.success('User added to the list.');
      return;
    }

    setUsers((prev) => prev.map((user) => (user.id === userData.id ? userData : user)));
  };

  const handleOpenAddUser = () => {
    setAttendeeForm({ mode: 'add', attendee: null });
  };

  const handleOpenEditUser = (user) => {
    setAttendeeForm({ mode: 'edit', attendee: user });
  };

  const handleBackToUpload = () => {
    setUsers([]);
    setWarnings([]);
    setSourceType(null);
    setUploadError('');
    setSelectedFile(null);
    setPage(0);
    extractUserDetailsMutation.reset();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 700,
            pr: 1,
          }}
        >
          Add Attendee
          <IconButton onClick={handleClose} disabled={isBusy} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ position: 'relative', minHeight: isReviewStep ? 320 : 220 }}>
          <Backdrop
            open={isBusy}
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.modal + 1,
              color: BRAND_ORANGE,
              bgcolor: 'rgba(255, 255, 255, 0.82)',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress color="inherit" size={40} />
              <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                {isSubmitting ? 'Adding attendees…' : 'Extracting attendee details…'}
              </Typography>
            </Box>
          </Backdrop>

          {!isReviewStep ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: 1.5 }}>
                Upload a CSV or Excel file containing a list of users with <strong>name</strong> and{' '}
                <strong>email</strong> columns. <strong>Phone number</strong> is optional. We will
                extract the details so you can review them before adding attendees to this event.
              </Alert>

              <ImageUpload
                label="Attendee file"
                accept={EXTRACT_USER_DETAILS_FILE_ACCEPT}
                formatHint={EXTRACT_USER_DETAILS_FORMAT_HINT}
                uploadLabel="Upload a CSV or Excel file"
                selectedFileName={selectedFile?.name ?? null}
                disabled={isBusy}
                error={Boolean(uploadError)}
                helperText={uploadError}
                validateFile={(file) => ({
                  valid: isExtractUserDetailsFileAllowed(file),
                  message: 'Please upload a CSV or Excel file (.csv, .xls, .xlsx).',
                })}
                onImageSelected={handleFileChange}
              />
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Review extracted attendees
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {users.length} attendee{users.length === 1 ? '' : 's'} found
                    {sourceType ? ` from ${sourceType.toUpperCase()}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddOutlinedIcon />}
                    onClick={handleOpenAddUser}
                    disabled={isBusy}
                    sx={{
                      textTransform: 'none',
                      bgcolor: BRAND_ORANGE,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
                    }}
                  >
                    Add User
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleBackToUpload}
                    disabled={isBusy}
                    sx={{ textTransform: 'none' }}
                  >
                    Upload another file
                  </Button>
                </Box>
              </Box>

              {warnings.length > 0 ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
                  {warnings.map((warning, index) => (
                    <Typography
                      key={`${warning}-${index}`}
                      variant="body2"
                      sx={{ lineHeight: 1.5 }}
                    >
                      {warning}
                    </Typography>
                  ))}
                </Alert>
              ) : null}

              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                  <PortalTableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell align="right" sx={{ width: 112 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </PortalTableHead>
                  <TableBody>
                    {paginatedUsers.map((user) => (
                      <PortalTableRow key={user.id}>
                        <PortalTableCell>{user.name || '—'}</PortalTableCell>
                        <PortalTableCell>{user.email || '—'}</PortalTableCell>
                        <PortalTableCell>{formatExtractedPhone(user.phoneNumber)}</PortalTableCell>
                        <PortalTableCell align="right">
                          <IconButton
                            size="small"
                            aria-label="Edit attendee"
                            onClick={() => handleOpenEditUser(user)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Delete attendee"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </PortalTableCell>
                      </PortalTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={users.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isBusy} sx={{ textTransform: 'none' }}>
            {isReviewStep ? 'Close' : 'Cancel'}
          </Button>
          {isReviewStep ? (
            <LoadingButton
              variant="contained"
              loading={isSubmitting}
              disabled={!users.length || isBusy}
              onClick={handleSubmitAttendees}
              sx={{
                textTransform: 'none',
                bgcolor: BRAND_ORANGE,
                boxShadow: 'none',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
              }}
            >
              Add attendees
            </LoadingButton>
          ) : null}
        </DialogActions>
      </Dialog>

      <EditAttendeeDialog
        open={Boolean(attendeeForm)}
        mode={attendeeForm?.mode ?? 'edit'}
        attendee={attendeeForm?.attendee}
        onClose={() => setAttendeeForm(null)}
        onSave={handleSaveAttendee}
      />
    </>
  );
};

export const GuestListAddAttendeeAction = ({ eventId, sx }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, ...sx }}>
        <Button
          variant="contained"
          startIcon={<PersonAddOutlinedIcon />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: BRAND_ORANGE,
            boxShadow: 'none',
            '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
          }}
        >
          Add Attendee
        </Button>
      </Box>
      <AddAttendeeModal open={open} onClose={() => setOpen(false)} eventId={eventId} />
    </>
  );
};

export default AddAttendeeModal;
